import React, { useState } from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { CompletionIndicator } from "./CompletionIndicator";
import { RequesterFields } from "./WithdrawPanel";
import { useApplication } from "./ApplicationProvider";
import { usePublicConfig } from "./PublicConfigProvider";
import { formatIsoDate, isAmendmentsOpen } from "../config";
import { PROOF_EXTENSIONS, requesterPhrase, withdrawalOpen, type WithdrawalSummary } from "../withdrawal";

function atText(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(iso));
}

/**
 * Round 50 — what a withdrawn application shows, every time it is opened
 * through its link. Reinstatement is self-service until AMENDMENTS_CLOSE_DATE
 * (owner, 2026-10-21); after that, the family contacts the program. Proof can
 * still be added while withdrawn (owner, A8.2). Amend, print sponsor copy and
 * the sponsor panel are deliberately absent.
 */
export function WithdrawnScreen() {
  const { data, draftToken, withdrawal, applyServerState } = useApplication();
  const cfg = usePublicConfig();
  const [requestedBy, setRequestedBy] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [proof, setProof] = useState<File | null>(null);
  const [proofMsg, setProofMsg] = useState("");

  const reinstateOpen = isAmendmentsOpen(cfg.amendmentsCloseDate);
  const proofOpen = withdrawalOpen(Date.now(), cfg.sessionEndDate);
  const requesterOk = (requestedBy === "parent" || requestedBy === "student") && name.trim().length > 0;

  const post = async (path: string, body: BodyInit, headers?: Record<string, string>) => {
    const res = await fetch(`/api/drafts/${encodeURIComponent(draftToken ?? "")}/${path}`, {
      method: "POST",
      body,
      headers,
    });
    const json = (await res.json().catch(() => ({}))) as {
      error?: string;
      status?: typeof data.meta.status;
      withdrawal?: WithdrawalSummary | null;
    };
    return { ok: res.ok, json };
  };

  const reinstate = async () => {
    if (!requesterOk || busy || !draftToken) return;
    setBusy(true);
    setError("");
    try {
      const { ok, json } = await post(
        "reinstate",
        JSON.stringify({ requestedBy, requesterName: name.trim() }),
        { "Content-Type": "application/json" }
      );
      if (!ok || !json.status) {
        setError(json.error || "Could not reinstate right now. Please try again shortly.");
        return;
      }
      applyServerState(json.status, null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("Could not reinstate right now. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  const addProof = async () => {
    if (!requesterOk || !proof || busy || !draftToken) return;
    const lower = proof.name.toLowerCase();
    if (!PROOF_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
      setProofMsg("The proof must be a PDF, JPG, or PNG file.");
      return;
    }
    setBusy(true);
    setProofMsg("");
    try {
      const form = new FormData();
      form.set("requestedBy", requestedBy);
      form.set("requesterName", name.trim());
      form.set("proof", proof);
      const { ok, json } = await post("withdrawal-proof", form);
      if (!ok) {
        setProofMsg(json.error || "Could not add that file right now.");
        return;
      }
      applyServerState("Withdrawn", json.withdrawal ?? withdrawal);
      setProof(null);
      setProofMsg("Thank you — the document was added and the program was notified.");
    } catch {
      setProofMsg("Could not add that file right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-8">
        <CompletionIndicator completionPercent={0} status="Withdrawn" />
        <div className="section-card" data-testid="withdrawn-screen">
          <h2 className="text-2xl font-semibold mb-2">Application withdrawn</h2>
          {data.meta.referenceNumber && (
            <p className="text-sm text-slate-600 mb-2">
              Reference number: <strong>{data.meta.referenceNumber}</strong>
            </p>
          )}
          {withdrawal ? (
            <p className="text-sm">
              Withdrawn on {atText(withdrawal.withdrawnAtUtc)} at the request of{" "}
              {requesterPhrase(withdrawal.requestedBy, withdrawal.requesterName)}. Reason: {withdrawal.reasonLabel}.
            </p>
          ) : (
            <p className="text-sm">This application has been withdrawn.</p>
          )}
          <p className="text-sm mt-2">
            Nothing has been deleted. A confirmation was emailed to <strong>{data.step2.studentEmail}</strong> and{" "}
            <strong>{data.step2.parentEmail}</strong>.
          </p>
        </div>

        <section className="section-card">
          <h2 className="text-xl font-semibold mb-2">Reinstate this application</h2>
          {reinstateOpen && draftToken ? (
            <>
              <p className="text-sm text-slate-600 mb-3">
                Changed your mind? Reinstate it until {formatIsoDate(cfg.amendmentsCloseDate)}. It goes back exactly
                where it was — nothing needs to be filled in again.
              </p>
              <RequesterFields
                idPrefix="reinstate"
                requestedBy={requestedBy}
                setRequestedBy={setRequestedBy}
                name={name}
                setName={setName}
              />
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
              <div className="mt-5 flex justify-end">
                <button type="button" className="btn-primary" disabled={!requesterOk || busy} onClick={() => void reinstate()}>
                  {busy ? "Working…" : "Reinstate application"}
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm">
              Online reinstatement closed on {formatIsoDate(cfg.amendmentsCloseDate)}. Contact{" "}
              <a className="underline text-program" href={`mailto:${cfg.supportEmail}`}>
                {cfg.supportEmail}
              </a>{" "}
              if this was a mistake.
            </p>
          )}
        </section>

        {proofOpen && draftToken && (
          <section className="section-card">
            <h2 className="text-lg font-semibold mb-2">Add proof of an emergency</h2>
            <p className="text-sm text-slate-600 mb-3">
              Not required. If the withdrawal is for an emergency and you now have a document (for example a
              doctor&apos;s note or police report), add it here — one PDF, JPG, or PNG at a time.
              {withdrawal && withdrawal.proofCount > 0 ? ` ${withdrawal.proofCount} document(s) received so far.` : ""}
            </p>
            {!reinstateOpen && (
              <div className="mb-3">
                <RequesterFields
                  idPrefix="proof"
                  requestedBy={requestedBy}
                  setRequestedBy={setRequestedBy}
                  name={name}
                  setName={setName}
                />
              </div>
            )}
            <input
              type="file"
              aria-label="Proof document"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={(e) => setProof(e.target.files?.[0] ?? null)}
            />
            {reinstateOpen && <p className="field-hint">Fill in who is asking and your name above first.</p>}
            {proofMsg && <p className="mt-2 text-sm text-slate-700">{proofMsg}</p>}
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                className="btn-secondary"
                disabled={!requesterOk || !proof || busy}
                onClick={() => void addProof()}
              >
                Add document
              </button>
            </div>
          </section>
        )}

      </main>
      <Footer />
    </div>
  );
}
